import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoTexto } from '@/componentes/Campo';
import { explicarFalha, pedir } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { primeiraMaiuscula } from '@/lib/texto';
import {
  AJUDA_DA_SENHA,
  COMPRIMENTO_MAXIMO_DA_SENHA,
  problemaObvioNaSenha,
  validarRepeticao,
  validarSenhaNoCampo,
} from '@/lib/senha';

/**
 * Trocar a própria senha (migração 0025), no perfil de quem está no sistema.
 *
 * A senha atual é pedida e conferida no banco, e a errada conta no freio da
 * entrada. As outras sessões da pessoa são encerradas; esta continua.
 */
export function TrocaDeSenha({ email }: { readonly email: string | null }) {
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [repeticao, setRepeticao] = useState('');
  const [problema, setProblema] = useState<string | null>(null);
  const [trocada, setTrocada] = useState(false);

  const trocar = useMutation({
    mutationFn: () => pedir<{ trocada: true }>('/sessao/senha', { method: 'PUT', body: JSON.stringify({ atual, nova }) }),
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Senha trocada',
        ...(email ? { alvo: { nome: email } } : {}),
        efeito: 'As suas outras sessões foram encerradas; esta continua',
      })),
    },
    onSuccess: () => {
      setAtual('');
      setNova('');
      setRepeticao('');
      setTrocada(true);
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setTrocada(false);
    if (atual === '') {
      setProblema('Informe a senha atual.');
      return;
    }
    const obvio = problemaObvioNaSenha(nova);
    if (obvio !== null) {
      setProblema(obvio);
      return;
    }
    if (nova !== repeticao) {
      setProblema('As duas senhas novas não são iguais. Digite a mesma senha nos dois campos.');
      return;
    }
    setProblema(null);
    trocar.mutate();
  }

  const recusa = trocar.isError ? explicarFalha(trocar.error) : null;

  return (
    /* GRADE DA R01 (p.28), e nao a coluna de 560 px da p.16, que a R01
       substituiu: este era o ULTIMO formulario no utilitario legado
       `coluna-formulario`, e por isso ele ficava numa coluna estreita dentro
       de um card de 1.600 px.
       Os tres campos de senha pedem `campo-linha`: eles ocupam a linha inteira
       porque senha nao se compara de lado a lado — por-los a dois por linha
       deixaria "Senha nova" e "Repita a senha nova" lado a lado, que e o par
       que mais se confunde. O que a grade resolve aqui e a largura do campo,
       nao o numero de colunas. */
    <form className="formulario-grade" onSubmit={enviar}>
      {/* O e-mail escondido é para o gerenciador de senhas do navegador
          atualizar a senha guardada do usuário certo. */}
      {email ? <input type="email" autoComplete="username" value={email} readOnly hidden /> : null}
      <div className="campo-linha">
        <CampoTexto rotulo="Senha atual" valor={atual} tipo="senha" autoComplete="current-password" maxLength={COMPRIMENTO_MAXIMO_DA_SENHA} aoMudar={setAtual} />
      </div>
      <div className="campo-linha">
      <CampoTexto
        rotulo="Senha nova"
        valor={nova}
        tipo="senha"
        autoComplete="new-password"
        maxLength={COMPRIMENTO_MAXIMO_DA_SENHA}
        ajuda={`${AJUDA_DA_SENHA} Trocar encerra as suas sessões abertas em outros computadores.`}
        aoMudar={setNova}
        validar={validarSenhaNoCampo}
      />
      </div>
      <div className="campo-linha">
      <CampoTexto
        rotulo="Repita a senha nova"
        valor={repeticao}
        tipo="senha"
        autoComplete="new-password"
        maxLength={COMPRIMENTO_MAXIMO_DA_SENHA}
        aoMudar={setRepeticao}
        validar={validarRepeticao(nova)}
      />
      </div>

      {problema !== null ? (
        <p role="alert" className="campo-linha txt-apoio text-tinta">
          {problema}
        </p>
      ) : null}
      {recusa !== null ? (
        <div role="alert" className="campo-linha flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{primeiraMaiuscula(recusa.erro)}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      ) : null}
      {trocada ? (
        <p role="status" className="campo-linha txt-apoio text-tinta">
          Senha trocada. As suas outras sessões foram encerradas.
        </p>
      ) : null}

      {/* A BARRA DE ACOES FIXA da R01 (p.28), que acompanha a grade: com o
          formulario na grade e a barra no estilo antigo, "Trocar senha" rolava
          junto com o conteudo. O portao H6 reprovou isso, e com razao. */}
      <div className="campo-linha barra-de-acoes">
        <Botao nivel="secundario" type="submit" disabled={trocar.isPending}>
          {trocar.isPending ? 'Trocando…' : 'Trocar senha'}
        </Botao>
      </div>
    </form>
  );
}
